// GE-CAPPS — helpers partagés AppRunner / Mes Apps / Catalogue.
//
// Formats de valeurs alignés sur la règle gridCols (LAP-551) : valueGetter =
// valeur BRUTE (tri/filtre), valueFormatter = affichage. Les types de colonnes
// viennent de la spec backend (CustomApps.sources) : text, number, money,
// percent, date, time, bool.
import React from 'react'
import {
  toNum,
  toDate,
  formatMoney,
  formatNumber,
  EMPTY,
} from '../Investments/_shared/gridCols'

// Périodes proposées par le moteur (CustomApps.spec.PERIODS) → libellés FR
// (repli i18n géré par l'appelant avec t(`customapps.period.${key}`, label)).
export const PERIODS = [
  ['all', 'Tout'],
  ['today', "Aujourd'hui"],
  ['this_week', 'Cette semaine'],
  ['this_month', 'Ce mois'],
  ['this_year', 'Cette année'],
  ['last_7_days', '7 jours'],
  ['last_30_days', '30 jours'],
  ['last_90_days', '90 jours'],
  ['last_6_months', '6 mois'],
  ['last_12_months', '12 mois'],
]

const formatDate = (d) => (d ? d.toLocaleDateString('fr-FR') : EMPTY)
const formatBool = (v) =>
  v === null || v === undefined ? EMPTY : v ? 'Oui' : 'Non'

/** Formatte une valeur brute selon le type déclaré par la spec. */
export const formatValue = (value, type) => {
  switch (type) {
    case 'money':
      return formatMoney(toNum(value))
    case 'number':
      return formatNumber(toNum(value))
    case 'percent': {
      const n = toNum(value)
      return n === null ? EMPTY : `${formatNumber(n)} %`
    }
    case 'date':
      return formatDate(toDate(value))
    case 'bool':
      return formatBool(value)
    case 'time':
      return value ? String(value).slice(0, 5) : EMPTY
    default:
      return value === null || value === undefined || value === ''
        ? EMPTY
        : String(value)
  }
}

/** Colonne DataGrid depuis une colonne de spec {field, label, type}. */
export const specCol = (col) => {
  const base = {
    field: col.field,
    headerName: col.label || col.field,
    flex: 1,
    minWidth: 110,
  }
  if (col.type === 'money' || col.type === 'number' || col.type === 'percent') {
    return {
      ...base,
      type: 'number',
      headerAlign: 'right',
      align: 'right',
      valueGetter: (p) => toNum(p.row[col.field]),
      valueFormatter: ({ value }) => formatValue(value, col.type),
    }
  }
  if (col.type === 'date') {
    return {
      ...base,
      type: 'date',
      valueGetter: (p) => toDate(p.row[col.field]),
      valueFormatter: ({ value }) => formatDate(value),
    }
  }
  if (col.type === 'bool') {
    return {
      ...base,
      type: 'boolean',
      valueGetter: (p) => p.row[col.field],
      valueFormatter: ({ value }) => formatBool(value),
    }
  }
  return {
    ...base,
    type: 'string',
    valueGetter: (p) => p.row[col.field],
    valueFormatter: ({ value }) => formatValue(value, col.type),
  }
}

/** Valeur KPI selon kpi.format (money/number/percent/text) + prefix/suffix. */
export const formatKpi = (value, kpi = {}) => {
  const inner =
    kpi.format === 'text'
      ? value === null || value === undefined
        ? EMPTY
        : String(value)
      : formatValue(value, kpi.format || 'number')
  return `${kpi.prefix || ''}${inner}${kpi.suffix || ''}`
}

// Palette des graphiques — mêmes teintes que la DA ca- (bleu en tête).
export const CHART_COLORS = [
  '#6fa0ff',
  '#5dcaa5',
  '#ef9f27',
  '#f0997b',
  '#a78bfa',
  '#22d3ee',
  '#f472b6',
  '#fbbf24',
  '#34d399',
  '#63648a',
]

// Icônes vectorielles au trait (même style que tabCatalog) — clés = valeurs
// possibles du champ `icon` d'une app (repli : grid).
export const APP_ICONS = {
  grid: (
    <svg viewBox="0 0 24 24">
      <rect x="3" y="3" width="7.5" height="7.5" rx="2" />
      <rect x="13.5" y="3" width="7.5" height="7.5" rx="2" />
      <rect x="3" y="13.5" width="7.5" height="7.5" rx="2" />
      <rect x="13.5" y="13.5" width="7.5" height="7.5" rx="2" />
    </svg>
  ),
  chart: (
    <svg viewBox="0 0 24 24">
      <path d="M4 20V4" />
      <path d="M4 20h16" />
      <path d="M7 15l4-4 3 3 5-6" />
    </svg>
  ),
  wallet: (
    <svg viewBox="0 0 24 24">
      <rect x="3" y="6" width="18" height="14" rx="3" />
      <path d="M3 10h18M16 15h2" />
    </svg>
  ),
  calendar: (
    <svg viewBox="0 0 24 24">
      <rect x="3" y="5" width="18" height="16" rx="3" />
      <path d="M8 3v4M16 3v4M3 10h18" />
    </svg>
  ),
  check: (
    <svg viewBox="0 0 24 24">
      <rect x="3" y="3" width="18" height="18" rx="4" />
      <path d="M8 12.5l3 3 5.5-6" />
    </svg>
  ),
  pie: (
    <svg viewBox="0 0 24 24">
      <path d="M12 3a9 9 0 109 9h-9z" />
      <path d="M14 2a9 9 0 018 8h-8z" />
    </svg>
  ),
  table: (
    <svg viewBox="0 0 24 24">
      <rect x="3" y="4" width="18" height="16" rx="2" />
      <path d="M3 9h18M9 9v11M15 9v11" />
    </svg>
  ),
  target: (
    <svg viewBox="0 0 24 24">
      <circle cx="12" cy="12" r="8.5" />
      <circle cx="12" cy="12" r="4.5" />
      <circle cx="12" cy="12" r="1" />
    </svg>
  ),
}

export const appIcon = (key) => APP_ICONS[key] || APP_ICONS.grid
